var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useMemo } from "react";
import { Icon, ChakraAvatar } from "../../ui";
/**
 * The Avatar component is used to represent user, and displays the profile picture, initials or fallback icon.
 * Avatar composes and `Chakra's Avatar component` and `Chakra's Box component`, so you can pass all its props.
 *
 * See more on  **Chakra API** or directly in [Chakra's documentation](https://chakra-ui.com/docs/components/avatar/usage)
 *
 * ```tsx
 * import { Avatar, AvatarProps } from '@springcare/sh-component-library'
 *
 * <Avatar name='Ryan Florence' src='https://bit.ly/ryan-florence'/>
 * <Avatar icon={VMomentsIcon}/>
 * ```
 *
 * ## Verdant API (prop)
 * - [Verdant's badge specification](https://www.figma.com/file/SwgpvBvow9GJAmtmKbXVN2/Badge)
 *
 * ### Size
 * - `md`
 * - `lg`
 * - `xl`
 * - `2xl`
 *
 * ### Type (ordered from most to least important)
 * - image
 * - text (name prop)
 * - icon (fallback)
 *
 * ### Color Scheme
 * - "base"
 * - "positive"
 * - "info"
 * - "warning"
 * - "negative"
 * - "neutral"
 *
 * #### Icon
 * - Off (icon undefined)
 * - On (icon defined, see [SVG Icon Reference](https://fantastic-broccoli-de639a7d.pages.github.io/?path=/docs/foundations-icons-icon-gallery--page))
 *
 *
 * <br>
 * <details>
 * <summary>**Chakra API**</summary>
 * <br>
 * Avatar composes and `Chakra's Avatar component` and `Chakra's Box props or Style Props`
 * - [Chakra Avatar Props](https://chakra-ui.com/docs/components/avatar/props)
 * - [Chakra Style Props](https://chakra-ui.com/docs/styled-system/style-props)
 *
 * </details>
 *
 * @override
 * @param {AvatarProps} props avatar props
 * @author Alejandro De la Cruz Tarin <adelacruz.ctr@springhealth.com>
 * @returns {React.ReactElement} Avatar
 * @see _avatarTheme button theme definition using chakra
 * @version 1.0.0
 */
export var Avatar = React.forwardRef(function (_a, ref) {
    var icon = _a.icon, _b = _a.size, size = _b === void 0 ? "md" : _b, _c = _a.variant, variant = _c === void 0 ? "medium-emphasis" : _c, avatarProps = __rest(_a, ["icon", "size", "variant"]);
    var iconElement = useMemo(function () {
        return icon ? (_jsx(Icon, { as: icon, boxSize: size === "md" ? "18px" : "24px" })) : undefined;
    }, [size, icon]);
    return (_jsx(ChakraAvatar, __assign({}, avatarProps, { ref: ref, icon: iconElement, variant: "v-".concat(variant), size: "v-".concat(size) })));
});
